import React, { useId } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Label } from 'cpool-components/general/Label';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { EMPLOYMENT_TYPES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useApplicantInfoContext } from '~/features/leadDetails/context/ApplicantInfoContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    onChange?: (value: string) => void;
    secondApplicant?: boolean;
};

const JobTermSelector: React.FC<Props> = ({ onChange, secondApplicant }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { applicantId } = useApplicantInfoContext();
    const { t } = useTranslation();
    const { control } = useFormContext();
    const labelId = useId();

    const options = Object.values(EMPLOYMENT_TYPES).map((value) => ({
        label: t(`EMPLOYMENT_TYPE.${value}`),
        value,
    }));

    const isDisabled = !canExecuteApplicantCommand({
        applicant: secondApplicant ? 2 : 1,
        commandName: LOAN_REQUEST_COMMANDS.changeApplicantEmployment,
        permissions,
    });

    return (
        <FormField<FieldValues, string | null> control={control} name="beschaeftigung.beschaeftigungsart">
            {({ error, onChange: onFieldChange, value }) => (
                <div className="flex items-start gap-2.5">
                    <Label className="h-8 w-[175px] shrink-0 justify-start" id={labelId}>
                        {t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.EMPLOYMENT_RELATIONSHIP')}
                    </Label>
                    <DropdownMenu
                        aria-labelledby={labelId}
                        data-testid={`beschaeftigung.beschaeftigungsart-${applicantId}`}
                        options={options}
                        value={value ?? undefined}
                        onChange={(newValue) => {
                            onFieldChange(newValue as string);
                            onChange?.(newValue as string);
                        }}
                        placeholder=""
                        triggerClassName="w-full justify-between"
                        className="w-full"
                        disabled={isDisabled}
                        error={error}
                        scrollable
                    />
                </div>
            )}
        </FormField>
    );
};

export default JobTermSelector;
